import React, { useState } from 'react'
import { Brain, TestTube, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import OpenAITestService from '@/utils/testOpenAI'

const OpenAITestPanel = () => {
  const [testResults, setTestResults] = useState(null)
  const [isRunning, setIsRunning] = useState(false)
  const [quickTestResult, setQuickTestResult] = useState(null)

  const runFullTest = async () => {
    setIsRunning(true)
    setTestResults(null)
    
    try {
      const testService = new OpenAITestService()
      const results = await testService.runAllTests()
      setTestResults(results)
    } catch (error) {
      console.error('Test failed:', error)
      setTestResults({ error: error.message })
    } finally {
      setIsRunning(false)
    }
  }

  const runQuickTest = async () => {
    setIsRunning(true)
    setQuickTestResult(null)
    
    try {
      const testService = new OpenAITestService()
      const result = await testService.quickTest()
      setQuickTestResult(result)
    } catch (error) {
      console.error('Quick test failed:', error)
      setQuickTestResult({ error: error.message })
    } finally {
      setIsRunning(false)
    }
  }

  const getStatusIcon = (success) => {
    if (success === null) return null
    return success ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    )
  }

  const getStatusText = (success) => {
    if (success === null) return 'Not tested'
    return success ? 'Passed' : 'Failed'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Brain className="w-6 h-6 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">OpenAI API Test Panel</h2>
        </div>
        <TestTube className="w-5 h-5 text-gray-400" />
      </div>

      {/* Quick Test Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Test</h3>
        <div className="flex items-center space-x-4">
          <button
            onClick={runQuickTest}
            disabled={isRunning}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <TestTube className="w-4 h-4" />
            )}
            <span>Run Quick Test</span>
          </button>
          
          {quickTestResult !== null && (
            <div className="flex items-center space-x-2">
              {getStatusIcon(quickTestResult)}
              <span className={`text-sm font-medium ${
                quickTestResult ? 'text-green-600' : 'text-red-600'
              }`}>
                {getStatusText(quickTestResult)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Full Test Section */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Comprehensive Test</h3>
        <button
          onClick={runFullTest}
          disabled={isRunning}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRunning ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <TestTube className="w-4 h-4" />
          )}
          <span>Run Full Test Suite</span>
        </button>
      </div>

      {/* Test Results */}
      {testResults && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Test Results</h3>
          
          {/* API Key Test */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">API Key Configuration</span>
              {getStatusIcon(testResults.apiKeyCheck?.success)}
            </div>
            {testResults.apiKeyCheck?.success ? (
              <div className="text-sm text-gray-600">
                <p>✅ API Key is properly configured</p>
                <p>Length: {testResults.apiKeyCheck.details.keyLength} characters</p>
                <p>Prefix: {testResults.apiKeyCheck.details.keyPrefix}</p>
              </div>
            ) : (
              <div className="text-sm text-red-600">
                ❌ API Key is missing or invalid
              </div>
            )}
          </div>

          {/* Content Generation Test */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Content Generation</span>
              {getStatusIcon(testResults.contentGeneration?.success)}
            </div>
            {testResults.contentGeneration?.success ? (
              <div className="text-sm text-gray-600">
                <p>✅ Content generation working</p>
                <p className="mt-1 p-2 bg-gray-50 rounded">
                  "{testResults.contentGeneration.content?.substring(0, 100)}..."
                </p>
                {testResults.contentGeneration.usage && (
                  <p className="mt-1 text-xs text-gray-500">
                    Tokens used: {testResults.contentGeneration.usage.total_tokens}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-sm text-red-600">
                ❌ Content generation failed: {testResults.contentGeneration?.error}
              </div>
            )}
          </div>

          {/* Image Generation Test */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Image Generation</span>
              {getStatusIcon(testResults.imageGeneration?.success)}
            </div>
            {testResults.imageGeneration?.success ? (
              <div className="text-sm text-gray-600">
                <p>✅ Image generation working</p>
                {testResults.imageGeneration.imageUrl && (
                  <div className="mt-2">
                    <img 
                      src={testResults.imageGeneration.imageUrl} 
                      alt="Generated test image"
                      className="w-32 h-32 object-cover rounded border"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-red-600">
                ❌ Image generation failed: {testResults.imageGeneration?.error}
              </div>
            )}
          </div>

          {/* Error Handling Test */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Error Handling</span>
              {getStatusIcon(testResults.errorHandling?.success)}
            </div>
            <div className="text-sm text-gray-600">
              {testResults.errorHandling?.success ? (
                <p>✅ Error handling working correctly</p>
              ) : (
                <p className="text-red-600">❌ Error handling failed</p>
              )}
            </div>
          </div>

          {/* Overall Status */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Status</span>
              {getStatusIcon(Object.values(testResults).every(test => test.success))}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {Object.values(testResults).every(test => test.success) 
                ? '✅ All tests passed! OpenAI service is working correctly.'
                : '❌ Some tests failed. Check the details above.'
              }
            </p>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Instructions</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Quick Test: Basic API connectivity and content generation</li>
          <li>• Full Test: Comprehensive testing of all OpenAI features</li>
          <li>• Check browser console for detailed logs</li>
          <li>• API keys are securely stored in Firebase Cloud Functions</li>
        </ul>
      </div>
    </div>
  )
}

export default OpenAITestPanel 